import {
  Cell,
  ColumnDef,
  ExpandedState,
  FilterFn,
  Header,
  Row,
  RowModel,
  RowSelectionState,
  SortingFn,
  Table,
  TableState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getMemoOptions,
  getPaginationRowModel,
  getSortedRowModel,
  memo,
  useReactTable,
} from "@tanstack/react-table";
import { ReactNode, isValidElement, useEffect, useMemo, useState } from "react";

import { withComputedColumnMaxSizes } from "./columnSizing";
import { toCsvCellValue } from "./csvExport";
import {
  fuzzyFilterFn,
  numberRangeFilterFn,
  rankGlobalFuzzy,
  withDerivedNumberFilters,
} from "./filterFns";
import { buildSortingFns } from "./sortFns";
import type {
  CsvExportColumn,
  CsvExportData,
  MeqCell,
  MeqCellContext,
  MeqColumnDef,
  MeqColumnVisibilityItem,
  MeqPaginationState,
  MeqRow,
  MeqRowData,
  MeqSortingState,
  MeqTableInstance,
  MeqTableProps,
} from "./types";

/**
 * THE v9 seam: the ONLY module in the codebase allowed to call
 * `useReactTable` (or import runtime values from `@tanstack/react-table`).
 * A future TanStack v8 → v9 migration must be a change to this file (plus
 * `types.ts`) and nothing else.
 *
 * Column defs are NOT transformed: `MeqColumnDef` is runtime-compatible with
 * TanStack's `ColumnDef` (the owned extras — `Cell`, `Header`, `filterVariant`
 * — are simply ignored by the engine and consumed by our render helpers), so a
 * single contained cast hands them to the engine untouched.
 */

const resolveUpdater = <T>(updater: T | ((old: T) => T), old: T): T =>
  typeof updater === "function" ? (updater as (old: T) => T)(old) : updater;

// MRT parity: accessor values that are already React elements (e.g. the
// PerformanceChart sweet-spot summary rows store "label + tooltip icon" JSX
// directly in the row data) render as-is — String() would show
// "[object Object]".
const toDisplayValue = (value: unknown): ReactNode =>
  value === null || value === undefined
    ? null
    : isValidElement(value)
      ? value
      : String(value);

/**
 * Wraps TanStack's built-in `getSortedRowModel` so that when no explicit
 * column sort is active AND a global filter is applied, rows are ordered by
 * fuzzy-match rank instead of their original order — ported from the old
 * MRT-based wrapper's `rankGlobalFuzzy` (that wrapper's
 * `enableGlobalFilterRankedResults` defaulted to `true`), so the closest
 * matches surface first, same as the old Table's default search behavior.
 * Falls back to the real sorted row model whenever a column sort IS active,
 * so explicit sorting always takes priority over fuzzy ranking.
 */
const getGlobalFilterRankedSortedRowModel = <TData extends MeqRowData>(): ((
  tableInstance: Table<TData>,
) => () => RowModel<TData>) => {
  const builtInFactory = getSortedRowModel<TData>();
  return (tableInstance) => {
    const getBuiltInRowModel = builtInFactory(tableInstance);
    return memo(
      () => [
        tableInstance.getState().sorting,
        tableInstance.getState().globalFilter,
        tableInstance.getPreSortedRowModel(),
      ],
      (sorting, globalFilter, rowModel) => {
        if (sorting.length > 0 || !globalFilter || !rowModel.rows.length) {
          return getBuiltInRowModel();
        }
        const rankedRows = [...rowModel.rows].sort(rankGlobalFuzzy);
        return { ...rowModel, rows: rankedRows, flatRows: rankedRows };
      },
      getMemoOptions(
        tableInstance.options,
        "debugTable",
        "getSortedRowModel",
        () => tableInstance._autoResetPageIndex(),
      ),
    );
  };
};

/**
 * Reads a header's `filterVariant` (owned column-def extra, MeqTable.tsx
 * stays free of `@tanstack/*` imports so this lookup lives in the seam).
 * Only "range" currently renders a header filter UI (NumberRangeFilter.tsx)
 * — "text" is declared on `MeqColumnDef` for forward-compat but unused
 * anywhere in the codebase today.
 */
export const getColumnFilterVariant = <TData extends MeqRowData>(
  header: Header<TData, unknown>,
): "range" | "text" | undefined =>
  (header.column.columnDef as MeqColumnDef<TData>).filterVariant;

/** Renders a header cell's content (string `header` or custom `Header`). */
export const renderHeaderContent = <TData extends MeqRowData>(
  header: Header<TData, unknown>,
): ReactNode => {
  if (header.isPlaceholder) {
    return null;
  }
  const meqColumnDef = header.column.columnDef as MeqColumnDef<TData>;
  if (meqColumnDef.Header !== undefined) {
    return typeof meqColumnDef.Header === "function"
      ? meqColumnDef.Header({ column: header.column })
      : meqColumnDef.Header;
  }
  return flexRender(header.column.columnDef.header, header.getContext());
};

/** Renders a body cell's content (custom `Cell` renderer or the raw value). */
export const renderCellContent = <TData extends MeqRowData>(
  cell: Cell<TData, unknown>,
): ReactNode => {
  const meqColumnDef = cell.column.columnDef as MeqColumnDef<TData>;
  const renderedCellValue = toDisplayValue(cell.getValue());
  if (meqColumnDef.Cell) {
    const cellContext: MeqCellContext<TData> = {
      cell: cell as MeqCell<TData>,
      row: cell.row,
      column: cell.column,
      renderedCellValue,
    };
    return meqColumnDef.Cell(cellContext);
  }
  return renderedCellValue;
};

/**
 * Leaf columns as CSV headers. Default (old-wrapper parity): ALL leaf
 * columns, ignoring the show/hide-columns menu — the old ExportDataToCsvButton
 * was handed the raw data array and never respected visibility. With
 * `shouldExportVisibleColumnsOnly`, only currently visible columns. Utility
 * columns (selection/expander/actions) aren't part of the engine's column
 * model at all (they're hardcoded cells — see the spike's learnings), so no
 * further filtering is needed here.
 */
export const getCsvExportColumns = <TData extends MeqRowData>(
  table: Table<TData>,
  shouldExportVisibleColumnsOnly = false,
): CsvExportColumn[] => {
  const exportColumns = shouldExportVisibleColumnsOnly
    ? table.getVisibleLeafColumns()
    : table.getAllLeafColumns();
  return exportColumns.map((column) => ({
    key: column.id,
    displayLabel: (column.columnDef as MeqColumnDef<TData>).header,
  }));
};

/**
 * One plain object per row (same column set as `getCsvExportColumns`),
 * across ALL filtered/sorted pages (`getPrePaginationRowModel`, same row
 * model the old wrapper's `exposeRowModel` exposed for this exact purpose),
 * each value run through `toCsvCellValue` ("formatted values" — arrays
 * joined, nullish blanked, `generateCsv`-friendly).
 */
export const getCsvExportRows = <TData extends MeqRowData>(
  table: Table<TData>,
  shouldExportVisibleColumnsOnly = false,
): Record<string, ReturnType<typeof toCsvCellValue>>[] => {
  const columnIds = getCsvExportColumns(
    table,
    shouldExportVisibleColumnsOnly,
  ).map((c) => c.key);
  return table
    .getPrePaginationRowModel()
    .rows.map((row) =>
      Object.fromEntries(
        columnIds.map((columnId) => [
          columnId,
          toCsvCellValue(row.getValue(columnId)),
        ]),
      ),
    );
};

const getCsvExportData = <TData extends MeqRowData>(
  table: Table<TData>,
  shouldExportVisibleColumnsOnly = false,
): CsvExportData => ({
  columns: getCsvExportColumns(table, shouldExportVisibleColumnsOnly),
  rows: getCsvExportRows(table, shouldExportVisibleColumnsOnly),
});

export const useMeqTableInstance = <TData extends MeqRowData>(
  props: MeqTableProps<TData>,
) => {
  const {
    columns,
    columnDataTypes,
    data,
    org,
    hasSorting = true,
    hasPagination = true,
    hasGlobalFilter = true,
    hasColumnHiding = true,
    hasRowSelection = false,
    hasMultiRowSelection = true,
    hasExpandMultiple = false,
    renderDetailPanel,
    isManualPagination = false,
    isManualSorting = false,
    isManualFiltering = false,
    shouldExportVisibleColumnsOnly = false,
    rowCount,
    state,
    initialState,
    sortingFns,
    filterFns,
    getRowId,
  } = props;

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const engineColumns = useMemo(
    // Contained cast: MeqColumnDef is runtime-compatible with ColumnDef (see
    // module docblock); the engine ignores the owned extras. Column-filter
    // derivation (old-wrapper parity: number columns get the range filter)
    // and column sizing (a missing `maxSize` derived from header length) are
    // computed before the cast — see filterFns.ts / columnSizing.ts.
    () =>
      withComputedColumnMaxSizes(
        withDerivedNumberFilters(columns, columnDataTypes),
      ) as unknown as ColumnDef<TData, unknown>[],
    [columns, columnDataTypes],
  );

  const table = useReactTable<TData>({
    columns: engineColumns,
    data,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel:
      hasSorting && !isManualSorting
        ? getGlobalFilterRankedSortedRowModel<TData>()
        : undefined,
    getFilteredRowModel: !isManualFiltering ? getFilteredRowModel() : undefined,
    getPaginationRowModel:
      hasPagination && !isManualPagination
        ? getPaginationRowModel()
        : undefined,
    enableSorting: hasSorting,
    enableGlobalFilter: hasGlobalFilter,
    enableHiding: hasColumnHiding,
    globalFilterFn: fuzzyFilterFn as FilterFn<TData>,
    enableRowSelection: hasRowSelection,
    enableMultiRowSelection: hasMultiRowSelection,
    enableExpanding: !!renderDetailPanel,
    getRowCanExpand: () => !!renderDetailPanel,
    manualPagination: isManualPagination,
    manualSorting: isManualSorting,
    manualFiltering: isManualFiltering,
    rowCount,
    sortingFns: {
      ...buildSortingFns<TData>(org),
      ...(sortingFns as Record<string, SortingFn<TData>> | undefined),
    },
    filterFns: {
      number: numberRangeFilterFn as FilterFn<TData>,
      fuzzy: fuzzyFilterFn as FilterFn<TData>,
      ...(filterFns as Record<string, FilterFn<TData>> | undefined),
    },
    // Selection + expansion are always hook-managed (owned state above);
    // pagination/sorting/globalFilter are only intercepted when the caller
    // controls that slice via `state` — TanStack treats a provided onXChange
    // as "externally managed", so passing one unconditionally would orphan
    // the engine's internal state.
    //
    // Single-select (hasMultiRowSelection: false) replicates the old
    // (now-deleted) MRT-based wrapper's handleRowSelectionChange EXACTLY: resolve
    // TanStack's updater against an EMPTY previous state instead of the real
    // one. row.toggleSelected's updater only ever sets/deletes the row it was
    // called on relative to "old", so seeding "old" with {} means selecting a
    // new row always yields { [row.id]: true } alone (any prior selection is
    // discarded) and deselecting the only selected row yields {} — the exact
    // radio-like behavior ~8 Administration/Management call sites depend on
    // (they read getSelectedRows()/getSelectedRowModel().flatRows and gate
    // Edit/Delete/UpdateRoles buttons on it containing exactly one row).
    // Multi-select resolves against the real previous state, as normal.
    onRowSelectionChange: (updater) => {
      const previousRowSelection = hasMultiRowSelection
        ? table.getState().rowSelection
        : {};
      const next = resolveUpdater(updater, previousRowSelection);
      setRowSelection(next);
      props.onRowSelectionChange?.(next);
    },
    onExpandedChange: (updater) => {
      setExpanded(resolveUpdater(updater, table.getState().expanded));
    },
    ...(state?.pagination
      ? {
          onPaginationChange: (
            updater:
              | MeqPaginationState
              | ((old: MeqPaginationState) => MeqPaginationState),
          ) => {
            props.onPaginationChange?.(
              resolveUpdater(updater, table.getState().pagination),
            );
          },
        }
      : {}),
    ...(state?.sorting
      ? {
          onSortingChange: (
            updater:
              | MeqSortingState
              | ((old: MeqSortingState) => MeqSortingState),
          ) => {
            props.onSortingChange?.(
              resolveUpdater(updater, table.getState().sorting),
            );
          },
        }
      : {}),
    ...(state?.globalFilter !== undefined
      ? {
          onGlobalFilterChange: (
            updater: string | ((old: string) => string),
          ) => {
            props.onGlobalFilterChange?.(
              resolveUpdater(updater, table.getState().globalFilter as string),
            );
          },
        }
      : {}),
    state: {
      rowSelection: state?.rowSelection ?? rowSelection,
      expanded: state?.expanded ?? expanded,
      ...(state?.pagination ? { pagination: state.pagination } : {}),
      ...(state?.sorting ? { sorting: state.sorting } : {}),
      ...(state?.globalFilter !== undefined
        ? { globalFilter: state.globalFilter }
        : {}),
      ...(state?.columnVisibility
        ? { columnVisibility: state.columnVisibility }
        : {}),
    },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 20 },
      ...(initialState as Partial<TableState> | undefined),
    },
  });

  // Old-wrapper parity (the old, now-deleted MRT-based wrapper's
  // `useEffect(() => { table.resetExpanded(); }, [table, data])`): collapse
  // every expanded row whenever the `data` array reference changes (e.g. a
  // query refetch after an edit/delete), so a stale expanded row id from the
  // previous dataset never lingers open against new data.
  //
  // The functional updater bails out (returns the SAME object) when nothing
  // is currently expanded, instead of always returning a fresh `{}`. This
  // matters because `data` isn't guaranteed to be a stable reference across
  // renders (many real call sites recompute it inline, e.g. `data.map(...)`,
  // without useMemo) — an unconditional `setExpanded({})` would then fire
  // this effect, trigger a state update to a new (but equal) object, cause a
  // re-render, and if that re-render recomputes `data` as yet another new
  // reference, the effect fires again — an infinite render loop. Returning
  // the existing reference when already empty lets React bail out via
  // Object.is and breaks that loop, while still resetting genuinely expanded
  // rows exactly once per real data change.
  useEffect(() => {
    setExpanded((previouslyExpanded) =>
      Object.keys(previouslyExpanded).length === 0 ? previouslyExpanded : {},
    );
  }, [data]);

  // Old-wrapper parity (the old, now-deleted MRT-based wrapper's `exposeRowModel`):
  // notify the caller with every row across ALL pages whenever that row
  // model changes. TanStack memoizes `getPrePaginationRowModel()`'s return
  // value, so `prePaginationRows` only gets a new reference when the
  // underlying data/sorting/filtering actually changed — same trigger
  // frequency as the old wrapper's own unmemoized
  // `table.getPrePaginationRowModel().flatRows` read in its render body.
  const prePaginationRows = table.getPrePaginationRowModel()
    .rows as MeqRow<TData>[];
  useEffect(() => {
    props.onRowModelChange?.(prePaginationRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mirrors the old wrapper's exact dep set (rows + the callback itself), not the whole `props` object
  }, [prePaginationRows, props.onRowModelChange]);

  /**
   * Old-wrapper parity: only one row expanded at a time unless
   * `hasExpandMultiple` is set.
   */
  const toggleRowExpanded = (row: Row<TData>) => {
    if (hasExpandMultiple) {
      row.toggleExpanded();
    } else {
      table.setExpanded({ [row.id]: !row.getIsExpanded() });
    }
  };

  const meqInstance: MeqTableInstance<TData> = useMemo(
    () => ({
      getSelectedRows: () =>
        table.getSelectedRowModel().flatRows as MeqRow<TData>[],
      getPrePaginationRowCount: () =>
        table.getPrePaginationRowModel().rows.length,
      resetRowSelection: () => table.resetRowSelection(),
      setGlobalFilter: (globalFilter: string) =>
        table.setGlobalFilter(globalFilter),
      getGlobalFilter: () => (table.getState().globalFilter as string) ?? "",
      getHideableColumns: (): MeqColumnVisibilityItem[] =>
        table
          .getAllLeafColumns()
          .filter((column) => column.getCanHide())
          .map((column) => ({
            id: column.id,
            header: (column.columnDef as MeqColumnDef<TData>).header,
            isVisible: column.getIsVisible(),
          })),
      toggleColumnVisibility: (columnId: string) =>
        table.getColumn(columnId)?.toggleVisibility(),
      getIsAllColumnsVisible: () => table.getIsAllColumnsVisible(),
      toggleAllColumnsVisible: (isVisible?: boolean) =>
        table.toggleAllColumnsVisible(isVisible),
      getCsvExportData: () =>
        getCsvExportData(table, shouldExportVisibleColumnsOnly),
    }),
    [table, shouldExportVisibleColumnsOnly],
  );

  return { table, meqInstance, toggleRowExpanded };
};
